import { CalendarIcon } from 'lucide-react'

import { useOnboardingStep } from 'components/hooks/employer_onboarding/useOnboardingStep'
import { TextLink, usePageTitle } from 'slab'

import { CenteredLayout } from '../CenteredLayout'
import { ContainerList } from '../ContainerList'
import { useEmployer } from '../EmployerProvider'
import { SupportLink } from '../SupportLink'
import { ConnectBanking } from './banking/ConnectBanking'
import { ConnectedAccount } from './banking/ConnectedAccount'

export function BankingPage () {
  usePageTitle('Connect your bank account')

  const { employer, refetch: refetchEmployer } = useEmployer()
  const { navigateToPreviousStep, navigateToNextStep } = useOnboardingStep()

  function renderContent () {
    if (employer?.has_plaid_account) {
      return (
        <>
          <hr className='my-6' />
          <ConnectedAccount
            onBack={navigateToPreviousStep}
            onNext={navigateToNextStep}
          />
        </>
      )
    }

    return (
      <div className='mt-16'>
        <ConnectBanking
          onBack={navigateToPreviousStep}
          onSuccess={async () => {
            await refetchEmployer()
            navigateToNextStep()
          }}
        />
      </div>
    )
  }

  return (
    <CenteredLayout>
      <CenteredLayout.Header
        heading='Let’s set up automatic payments'
        subheading='We’ll use your bank account to make sure you never miss a payment. You can expect the following:'
      />
      <ContainerList>
        <ContainerList.Item
          icon={CalendarIcon}
          heading='On the 1st of each month'
          layout='centered'
        >
          We’ll debit platform fees.
        </ContainerList.Item>
        <ContainerList.Item
          icon={CalendarIcon}
          heading='On the 15th of each month'
          layout='centered'
        >
          We’ll debit healthcare costs (premiums and eligible medical
          expenses) in advance.
        </ContainerList.Item>
      </ContainerList>
      {renderContent()}
      <div className='mt-16 text-center'>
        <SupportLink />
      </div>
      <div className='mt-16 space-y-4 text-gray-500'>
        <p>
          Thatch uses Plaid to securely connect your bank account.
        </p>
        <p>
          By connecting your bank account, you authorize Thatch Health inc to
          debit the bank account specified above for any amount owed for charges
          arising from your use of Thatch's services and/or purchase of products
          from Thatch, pursuant to Thatch's employer{' '}
          <TextLink
            href='https://thatch.com/platform-agreement'
            target='_blank'
            rel='noopener'
          >
            Platform Agreement
          </TextLink>
          , until this authorization is revoked. You may amend or cancel this
          authorization at any time by reaching out to{' '}
          <TextLink
            href='mailto:support@thatch.com'
            rel='noopener'
            target='_blank'
          >
            support@thatch.com
          </TextLink>
          .
        </p>
        <p>
          If you use Thatch's services or purchase additional productions
          periodically pursuant to our terms of use, you authorize Thatch to
          debit your bank account periodically. Payments that fall outside of
          the regular debits authorized above will only be debited after your
          authorization is obtained.
        </p>
      </div>
    </CenteredLayout>
  )
}
